import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, X, ChevronRight, ExternalLink, Mail, AlertCircle } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { computeAllClientAlerts, ALERT_STYLES } from './alerts'
import TaskModal from './TaskModal'
import TaskComments from './TaskComments'
import { TaskTimerButton } from './TaskTimer'
import { isActiveProfile, DONE_STATUSES, CLIENT_APPROVED_STATUSES, BLOG_PUBLISH_WARNING } from './constants'
import { SOCIAL_FLOW, sendRevisionToWorker, reopenReviewAfterResubmit } from './workflowRevisions'

const SOCIAL_PHASES = [
  { value: 'drafting',      label: 'Building',       icon: '📱', color: '#2a6e84', bg: '#e8f5fa' },
  { value: 'review',        label: 'Internal Review', icon: '👁', color: '#8c4d78', bg: '#f9edf5' },
  { value: 'client_review', label: 'Client Review',  icon: '⏳', color: '#c4962a', bg: '#fff8e1' },
  { value: 'scheduling',    label: 'Scheduling',     icon: '📅', color: '#2e6e3a', bg: '#e8f5eb' },
  { value: 'complete',      label: 'Complete',       icon: '✅', color: '#2e6e3a', bg: '#e8f5eb' },
]
const getPhase = v => SOCIAL_PHASES.find(p => p.value === v)

export const SOCIAL_PLANS = [
  { id: 'startup',   label: 'Start Up (10/mo)',       carousels: 5, reels: 2, statics: 4 },
  { id: 'small',     label: 'Small Business (15/mo)', carousels: 7, reels: 4, statics: 4 },
  { id: 'corporate', label: 'Corporate (20/mo)',      carousels: 9, reels: 6, statics: 5 },
]

// Reference docs that show up on every batch detail
const SOCIAL_REFERENCE_DOCS = [
  { label: '2026 Social Batch Guide',     url: 'https://docs.google.com/document/d/1nUz0EH3GZi-tmvfH200eiK2GICKlm_FE7o5CjQ_xHfo/edit?usp=sharing' },
  { label: '2026 Carousel Posts Guide',   url: 'https://docs.google.com/document/d/1hGARUsx5v_GCQWtOjIIrGtDnWQCBRUOb9wh3u9W4XGk/edit?usp=sharing' },
  { label: '2026 Reels Guide',            url: 'https://docs.google.com/document/d/1VhG0dsJTPq9GwlPJy00IvRF8Gz9CNxpcyy_-56t5K5o/edit?usp=sharing' },
]

// Quality checklists per phase
const BUILD_CHECKLIST = [
  { id: 'typos',         label: 'Checked for typos across all posts' },
  { id: 'urls',          label: 'All URLs and Instagram handles are correct' },
  { id: 'not_ai',        label: 'Nothing reads like it was AI-generated' },
  { id: 'carousel_hook', label: 'Each carousel has a strong hook on slide 1' },
  { id: 'carousel_slides', label: 'Each carousel is 7–10 slides, one idea per slide' },
  { id: 'reels_length',  label: 'Reels are 7–15 seconds with hook in the first 3 seconds' },
  { id: 'captions',      label: 'Captions are substantive (not too short, not too long)' },
  { id: 'ctas',          label: 'CTAs are clear (save / share / comment / read more)' },
  { id: 'consistency',   label: 'Design is consistent across the batch' },
  { id: 'taco_bell',     label: 'Each post is recognizable as the client (not generic)' },
]

const SCHEDULE_CHECKLIST = [
  { id: 'uploaded',     label: 'All assets uploaded to Content Studio (or scheduling tool)' },
  { id: 'captions',     label: 'Captions copied correctly with hashtags' },
  { id: 'audio',        label: 'Trending audio added to reels (manual-post reels flagged)' },
  { id: 'times',        label: 'Posting times set per client preference' },
  { id: 'hashtags',     label: 'Hashtags appropriate for each post' },
  { id: 'holidays',     label: 'Holidays / client opt-outs respected' },
  { id: 'phone_check',  label: 'Final visual scan on phone preview' },
  { id: 'fb_linked',    label: 'Facebook page is linked and will cross-post correctly' },
]

// ─── Field component (outside modal to prevent focus loss) ──────────────────
function BatchField({ label, k, placeholder, rows, hint, type, form, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{hint}</div>}
      {rows
        ? <textarea className="form-control" rows={rows} value={form[k] || ''} onChange={e => onChange(k, e.target.value)} placeholder={placeholder} />
        : <input className="form-control" type={type || 'text'} value={form[k] || ''} onChange={e => onChange(k, e.target.value)} placeholder={placeholder} />
      }
    </div>
  )
}

// ─── New Social Batch Modal ─────────────────────────────────────────────────
function NewBatchModal({ clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const defaultPlan = SOCIAL_PLANS.find(p => p.id === 'startup')
  const [form, setForm] = useState({
    client_id: '', assigned_to: '', scheduler_id: '',
    month: format(new Date(), 'yyyy-MM'),
    plan: 'startup',
    carousel_count: defaultPlan.carousels,
    reel_count: defaultPlan.reels,
    static_count: defaultPlan.statics,
    whats_working: '', whats_not_working: '', brief_notes: '',
    draft_due_date: '', target_publish_date: '',
    client_email: '',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Auto-fill counts when plan changes
  const handlePlanChange = (planId) => {
    const plan = SOCIAL_PLANS.find(p => p.id === planId)
    setForm(f => ({
      ...f,
      plan: planId,
      carousel_count: plan.carousels,
      reel_count: plan.reels,
      static_count: plan.statics,
    }))
  }

  const save = async () => {
    if (!form.client_id) { toast('Select a client', 'error'); return }
    if (!form.assigned_to) { toast('Select a creator', 'error'); return }
    if (!form.month) { toast('Set a month', 'error'); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        carousel_count: parseInt(form.carousel_count) || 0,
        reel_count: parseInt(form.reel_count) || 0,
        static_count: parseInt(form.static_count) || 0,
        scheduler_id: form.scheduler_id || null,
        draft_due_date: form.draft_due_date || null,
        target_publish_date: form.target_publish_date || null,
        assigned_by: currentUser.id,
        status: 'drafting',
      }

      const { data: batch, error: batchError } = await supabase.from('social_batches').insert(payload).select().single()
      if (batchError) throw batchError

      const client = clients.find(c => c.id === form.client_id)
      const clientName = client?.name || 'Client'
      const monthLabel = form.month
      const planLabel = SOCIAL_PLANS.find(p => p.id === form.plan)?.label || form.plan

      // Create the 4 phase tasks
      const assignedBy = currentUser.id
      const tasks = [
        {
          title: `📱 Build Social Batch: ${monthLabel} — ${clientName}`,
          client_id: form.client_id,
          assigned_to: form.assigned_to,
          assigned_by: assignedBy,
          date_due: form.draft_due_date || null,
          status: 'not_started',
          service_area: 'social',
          approval_path: 'internal',
          social_batch_id: batch.id,
          social_phase: 'build',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: BUILD_CHECKLIST.map(i => ({ ...i, checked: false })),
          description: `Build ${form.carousel_count} carousels, ${form.reel_count} reels, ${form.static_count} statics for ${monthLabel}. Plan: ${planLabel}.`,
        },
        {
          title: `👁 Review Social Batch: ${monthLabel} — ${clientName}`,
          client_id: form.client_id,
          assigned_to: assignedBy,
          assigned_by: assignedBy,
          date_due: form.draft_due_date || null,
          status: 'not_started',
          service_area: 'social',
          approval_path: 'internal_then_client_fyi',
          social_batch_id: batch.id,
          social_phase: 'review',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: [],
          description: 'Review the creator submission and approve or request revisions before sending to client.',
        },
        {
          title: `📨 Send to Client: ${monthLabel} — ${clientName}`,
          client_id: form.client_id,
          assigned_to: assignedBy,
          assigned_by: assignedBy,
          date_due: form.target_publish_date || null,
          status: 'not_started',
          service_area: 'social',
          approval_path: 'internal_client_live',
          social_batch_id: batch.id,
          social_phase: 'client_review',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: [],
          description: `Send batch to ${clientName} for approval. Client email: ${form.client_email || 'not set'}`,
          notes: `Client email: ${form.client_email || 'check client profile'}`,
        },
        {
          title: `📅 Schedule Social Batch: ${monthLabel} — ${clientName}`,
          client_id: form.client_id,
          assigned_to: form.scheduler_id || assignedBy,
          assigned_by: assignedBy,
          date_due: form.target_publish_date || null,
          status: 'not_started',
          service_area: 'social',
          approval_path: 'none',
          social_batch_id: batch.id,
          social_phase: 'schedule',
          client_visible: false,
          collaborator_ids: [],
          deliverable_links: [],
          google_doc_links: [],
          loom_links: [],
          checklist_items: SCHEDULE_CHECKLIST.map(i => ({ ...i, checked: false })),
          description: 'Schedule the approved batch in Content Studio. Run through the pre-schedule QC checklist before scheduling.',
        },
      ]

      const { data: createdTasks, error: tasksError } = await supabase.from('tasks').insert(tasks).select()
      if (tasksError) throw tasksError

      // Notify creator
      await supabase.from('notifications').insert({
        user_id: form.assigned_to,
        task_id: createdTasks?.[0]?.id,
        type: 'comment',
        message: `New social batch assignment: ${monthLabel} for ${clientName}`,
      })

      toast('Social batch created! 4 tasks generated.', 'success')
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const STEPS = ['Basics', 'Counts', 'Brief']

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">New Social Batch</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
              {STEPS.map((s, i) => (
                <button key={i} onClick={() => setStep(i + 1)}
                  style={{ padding: '3px 10px', borderRadius: 50, fontSize: 11, border: 'none', cursor: 'pointer', fontWeight: step === i + 1 ? 700 : 400, background: step === i + 1 ? 'var(--navy)' : 'var(--cream-dark)', color: step === i + 1 ? '#fff' : 'var(--text-muted)' }}>
                  {i + 1}. {s}
                </button>
              ))}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>1. Assignment Basics</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Client Email</label>
                  <input className="form-control" value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="client@practice.com" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Creator (Assigned To) *</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                    <option value="">Select creator...</option>
                    {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Scheduler (for scheduling phase)</label>
                  <select className="form-control" value={form.scheduler_id} onChange={e => set('scheduler_id', e.target.value)}>
                    <option value="">Pick later</option>
                    {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <BatchField label="Month *" k="month" type="month" form={form} onChange={set} />
                <BatchField label="Draft Due Date" k="draft_due_date" type="date" hint="When creator should submit" form={form} onChange={set} />
              </div>
              <BatchField label="Target First-Post Date" k="target_publish_date" type="date" hint="When the first post should go live" form={form} onChange={set} />
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>2. Plan & Post Counts</div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-control" value={form.plan} onChange={e => handlePlanChange(e.target.value)}>
                  {SOCIAL_PLANS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Auto-filled from plan — editable
                </div>
                <div className="form-row">
                  <BatchField label="Carousels" k="carousel_count" type="number" form={form} onChange={set} />
                  <BatchField label="Reels" k="reel_count" type="number" form={form} onChange={set} />
                  <BatchField label="Statics" k="static_count" type="number" form={form} onChange={set} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Total: {(parseInt(form.carousel_count) || 0) + (parseInt(form.reel_count) || 0) + (parseInt(form.static_count) || 0)} posts
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--navy)', marginBottom: 14 }}>3. Brief & Recent Performance</div>
              <BatchField label="What's working recently" k="whats_working" rows={3}
                placeholder="e.g. Carousel on attachment styles got 2k saves. Reels with no face are outperforming face reels..." form={form} onChange={set} />
              <BatchField label="What's NOT working" k="whats_not_working" rows={3}
                placeholder="e.g. Quote statics are flopping, motivational posts feel too generic..." form={form} onChange={set} />
              <BatchField label="Brief notes" k="brief_notes" rows={4}
                placeholder="Any specific topics, holidays, campaigns to incorporate this month..." form={form} onChange={set} />
            </>
          )}
        </div>

        <div className="modal-footer">
          <div style={{ marginRight: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>Step {step} of {STEPS.length}</div>
          {step > 1 && <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>← Back</button>}
          {step < STEPS.length
            ? <button className="btn btn-gold" onClick={() => setStep(s => s + 1)}>Next →</button>
            : <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Creating...' : '🚀 Create Social Batch'}</button>
          }
        </div>
      </div>
    </div>
  )
}

// Safe date formatter
function safeFmt(dateStr, fmt) {
  if (!dateStr) return null
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return format(d, fmt)
  } catch { return null }
}

// Turn URLs in plain text into clickable links. Splits on http(s):// or www. URLs.
function linkify(text) {
  if (!text) return null
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      const href = part.startsWith('http') ? part : `https://${part}`
      return (
        <a key={i} href={href} target="_blank" rel="noreferrer"
          style={{ color: '#9ed1e8', textDecoration: 'underline', wordBreak: 'break-all' }}
          onClick={e => e.stopPropagation()}>
          {part}
        </a>
      )
    }
    return part
  })
}

// ─── Social Phase Task (mirrors BlogPhaseTask exactly) ──────────────────────
export function SocialPhaseTask({ task, batch, profiles, clients, currentUser, onUpdated, onOpenInPanel, focused }) {
  const toast = useToast()
  const [checklist, setChecklist] = useState(task.checklist_items || [])
  const [saving, setSaving] = useState(false)
  const [newDocLink, setNewDocLink] = useState('')
  const [linkLabel, setLinkLabel] = useState('')
  const [localLinks, setLocalLinks] = useState(task.deliverable_links || [])
  const [showRevisionInput, setShowRevisionInput] = useState(false)
  const [revisionNote, setRevisionNote] = useState('')
  const [localStatus, setLocalStatus] = useState(task.status)
  const [showEdit, setShowEdit] = useState(false)

  // Keep state synced if task prop changes (e.g., when side panel updates)
  useEffect(() => {
    setChecklist(task.checklist_items || [])
    setLocalLinks(task.deliverable_links || [])
    setLocalStatus(task.status)
  }, [task.id, task.status, task.checklist_items, task.deliverable_links])

  // Step 4 only: look up the step 3 (client_review) task so we can warn the
  // scheduler until the client has approved (same rule as blogs; warn only).
  const [clientApproved, setClientApproved] = useState(null) // null = unknown / not applicable
  useEffect(() => {
    if (task.social_phase !== 'schedule' || !task.social_batch_id) { setClientApproved(null); return }
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.from('tasks').select('status')
        .eq('social_batch_id', task.social_batch_id)
        .eq('social_phase', 'client_review')
        .maybeSingle()
      if (cancelled) return
      setClientApproved(error || !data ? null : CLIENT_APPROVED_STATUSES.includes(data.status))
    })()
    return () => { cancelled = true }
  }, [task.id, task.social_phase, task.social_batch_id, task.status])
  const showClientWarning = task.social_phase === 'schedule' && clientApproved === false && !DONE_STATUSES.includes(localStatus)

  const allChecked = checklist.length > 0 && checklist.every(i => i.checked)
  const checkedCount = checklist.filter(i => i.checked).length

  const updateStatus = async (newStatus, opts = {}) => {
    setSaving(true)
    await supabase.from('tasks').update({ status: newStatus }).eq('id', task.id)
    setLocalStatus(newStatus)

    // Creator submits (or resubmits) → batch shows "Internal Review", and if the
    // AD's review step was waiting on revisions it goes back to Ready for Review.
    if (task.social_phase === 'build' && newStatus === 'ready_for_review' && task.social_batch_id) {
      await supabase.from('social_batches').update({ status: 'review' }).eq('id', task.social_batch_id)
      await reopenReviewAfterResubmit(SOCIAL_FLOW, task)
    }

    // Client approved → hand off to the scheduler: flip their task from
    // 'not_started' to 'goes_live' ("Ready to Deploy"), same as blogs.
    if (task.social_phase === 'client_review' && ['approved', 'live'].includes(newStatus) && task.social_batch_id) {
      await supabase.from('tasks').update({ status: 'goes_live' })
        .eq('social_batch_id', task.social_batch_id)
        .eq('social_phase', 'schedule')
        .eq('status', 'not_started')
    }

    // Advance parent batch status when this phase is approved
    if (['approved', 'live'].includes(newStatus)) {
      const nextPhaseStatus = {
        build: 'review',
        review: 'client_review',
        client_review: 'scheduling',
        schedule: 'complete',
      }[task.social_phase]
      if (nextPhaseStatus) {
        await supabase.from('social_batches').update({ status: nextPhaseStatus }).eq('id', task.social_batch_id)
      }
      // The Review phase IS the review of the Build — so approving Review means the
      // Build is done too. Mark the sibling build task approved so it doesn't strand
      // at "ready for review" (regardless of whether one person did both).
      if (task.social_phase === 'review' && task.social_batch_id) {
        await supabase.from('tasks').update({ status: 'approved' })
          .eq('social_batch_id', task.social_batch_id).eq('social_phase', 'build')
      }
    }

    // Notify the right person
    const notifyId = newStatus === 'ready_for_review' ? task.assigned_by : task.assigned_to
    if (notifyId && notifyId !== currentUser.id) {
      const msgs = {
        ready_for_review: `"${task.title}" is ready for review`,
        approved: `"${task.title}" has been approved`,
        needs_revision: `"${task.title}" needs revisions`,
        in_progress: `"${task.title}" has been started`,
      }
      await supabase.from('notifications').insert({ user_id: notifyId, task_id: task.id, type: newStatus, message: msgs[newStatus] || `"${task.title}" status updated` })
    }

    if (!opts.silent) toast(newStatus === 'ready_for_review' ? 'Submitted for review!' : newStatus === 'approved' ? 'Approved!' : newStatus === 'needs_revision' ? 'Sent back for revisions' : 'Status updated', 'success')
    onUpdated()
    setSaving(false)
  }

  // Review step (AD) or client step: send the work back to the creator so it
  // lands in THEIR tasks as Needs Revision. Client revisions also reset this
  // client step to Not Started and re-open the AD's review step.
  const submitRevision = async () => {
    const note = revisionNote.trim()
    if (!note) { toast('Please explain what needs to be revised', 'error'); return }
    setSaving(true)
    const isClient = task.social_phase === 'client_review'
    await supabase.from('task_comments').insert({ task_id: task.id, author_id: currentUser.id, content: `${isClient ? '↩ Client requested revisions' : '↩ Revision requested'}: ${note}`, mentions: [] })
    const { error } = await sendRevisionToWorker(SOCIAL_FLOW, task, note, currentUser.id, isClient ? 'client' : 'internal')
    if (error) {
      toast('Could not send back to the creator: ' + error.message, 'error')
      setSaving(false)
      return
    }
    await updateStatus(isClient ? 'not_started' : 'needs_revision', { silent: true })
    toast('Sent back to the creator for revisions', 'success')
    setShowRevisionInput(false)
    setRevisionNote('')
    setSaving(false)
  }

  const toggleItem = async (id) => {
    const updated = checklist.map(i => i.id === id ? { ...i, checked: !i.checked } : i)
    setChecklist(updated)
    await supabase.from('tasks').update({ checklist_items: updated }).eq('id', task.id)
  }

  const addDocLink = async () => {
    if (!newDocLink.trim()) return
    const newEntry = linkLabel.trim() ? `${linkLabel.trim()} | ${newDocLink.trim()}` : newDocLink.trim()
    const updated = [...localLinks, newEntry]
    setLocalLinks(updated)
    setNewDocLink('')
    setLinkLabel('')
    await supabase.from('tasks').update({ deliverable_links: updated }).eq('id', task.id)
    toast('Link added!', 'success')
  }

  const removeLink = async (idx) => {
    const updated = localLinks.filter((_, i) => i !== idx)
    setLocalLinks(updated)
    await supabase.from('tasks').update({ deliverable_links: updated }).eq('id', task.id)
  }

  const sendToClient = () => {
    // Pull all deliverable links from across the batch's tasks (the build task usually has them)
    const linkLines = localLinks.length > 0
      ? localLinks.map(l => {
          const [maybeLabel, maybeUrl] = l.includes(' | ') ? l.split(' | ') : [null, l]
          return maybeUrl ? `• ${maybeLabel}: ${maybeUrl}` : `• ${l}`
        }).join('\n')
      : 'Links not yet attached.'
    const clientName = clients.find(c => c.id === task.client_id)?.name || 'your practice'
    const monthLabel = batch?.month || ''
    const subject = encodeURIComponent(`Your ${monthLabel} Social Media Batch is Ready for Review`)
    const body = encodeURIComponent(
      `Hi,\n\n` +
      `Your social media batch for ${monthLabel} is ready for your review! This includes ${batch?.carousel_count || 0} carousels, ${batch?.reel_count || 0} reels, and ${batch?.static_count || 0} static posts.\n\n` +
      `📱 Batch deliverables:\n${linkLines}\n\n` +
      `Please review and let us know:\n• ✅ Approved as-is — we'll get it scheduled\n• ✏️ Revisions needed (please add comments directly in Canva or reply with notes)\n\n` +
      `Looking forward to your feedback!\n\nBest,\n${profiles.find(p => p.id === currentUser.id)?.full_name || 'LMR Team'}`
    )
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(batch?.client_email || '')}&su=${subject}&body=${body}`, '_blank')
  }

  const statusColors = {
    not_started: { bg: 'var(--cream-dark)', color: 'var(--text-muted)' },
    in_progress: { bg: '#e8f5fa', color: '#2a6e84' },
    ready_for_review: { bg: '#fff8e1', color: '#8a6000' },
    needs_revision: { bg: '#fff8e1', color: '#c4962a' },
    goes_live: { bg: '#f9edf5', color: '#8c4d78' },
    approved: { bg: '#e8f5eb', color: '#2e6e3a' },
    live: { bg: '#e8f5eb', color: '#2e6e3a' },
  }
  const sc = statusColors[localStatus] || statusColors.not_started

  return (
    <div style={{ background: '#fff', border: '1px solid var(--border-light)', borderRadius: 14, padding: 18, marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{task.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {profiles.find(p => p.id === task.assigned_to)?.full_name || 'Unassigned'}
            {task.assigned_by && task.assigned_by !== task.assigned_to && (
              <> · Assigned by {profiles.find(p => p.id === task.assigned_by)?.full_name || 'unknown'}</>
            )}
            {task.date_due && !isNaN(new Date(task.date_due)) && ` · Due ${safeFmt(task.date_due, 'MMM d')}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <button onClick={() => setShowEdit(true)} title="Edit task (dates, assignee, collaborators)"
            style={{ background: 'var(--cream)', border: '1px solid var(--border-light)', color: 'var(--text-secondary)', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            ✎ Edit
          </button>
          {onOpenInPanel && (
            <button onClick={onOpenInPanel} title="Open in side panel"
              style={{ background: 'var(--cream)', border: '1px solid var(--border-light)', color: 'var(--text-secondary)', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              ⇲ Panel
            </button>
          )}
          <span style={{ fontSize: 11, padding: '3px 12px', borderRadius: 50, background: sc.bg, color: sc.color, fontWeight: 600 }}>
            {localStatus.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Step 4 warning: client hasn't approved step 3 yet (warn only, buttons still work) */}
      {showClientWarning && (
        <div role="alert" style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{BLOG_PUBLISH_WARNING}</span>
        </div>
      )}

      {/* Brief reminder on build phase */}
      {task.social_phase === 'build' && batch && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--navy)', borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Your Assignment</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#fff', marginBottom: 4 }}>
            {batch.month} batch — {batch.carousel_count} carousels, {batch.reel_count} reels, {batch.static_count} statics
          </div>
          {batch.whats_working && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>✓ Working: {linkify(batch.whats_working)}</div>}
          {batch.whats_not_working && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>✗ Not working: {linkify(batch.whats_not_working)}</div>}
          {batch.brief_notes && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>📝 Notes: {linkify(batch.brief_notes)}</div>}
        </div>
      )}

      {/* Deliverable links — always shown */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
          🔗 Deliverable Links (Canva, drive folders, etc.)
        </div>
        {localLinks.map((link, i) => {
          const hasLabel = link.includes(' | ')
          const [maybeLabel, maybeUrl] = hasLabel ? link.split(' | ') : [null, link]
          const displayUrl = maybeUrl || link
          return (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
              <a href={displayUrl} target="_blank" rel="noreferrer" className="link-tag" style={{ background: '#e8f5fa', color: '#2a6e84', display: 'inline-flex', flex: 1 }}>
                <ExternalLink size={11} /><span>{hasLabel ? maybeLabel : 'Open Link'}</span>
              </a>
              <button onClick={() => removeLink(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}><X size={12} /></button>
            </div>
          )
        })}
        {localLinks.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No links yet</span>}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)}
            placeholder="Label (e.g. June Canva)"
            style={{ width: 140, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none' }} />
          <input value={newDocLink} onChange={e => setNewDocLink(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addDocLink()}
            placeholder="Paste URL..."
            style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none' }} />
          <button onClick={addDocLink} style={{ padding: '6px 12px', background: '#e8f5fa', color: '#2a6e84', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Add</button>
        </div>
      </div>

      {/* Action buttons — by phase + status */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: checklist.length > 0 ? 12 : 0 }}>

        {/* BUILD PHASE */}
        {task.social_phase === 'build' && localStatus === 'not_started' && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start Building</button>
        )}
        {task.social_phase === 'build' && localStatus === 'in_progress' && (
          <button className="btn btn-sm btn-gold" onClick={() => {
            if (checklist.length > 0 && !allChecked) { toast(`Complete all ${checklist.length} checklist items first (${checkedCount} done)`, 'error'); return }
            if (localLinks.length === 0) { toast('Add at least one deliverable link first', 'error'); return }
            updateStatus('ready_for_review')
          }} disabled={saving}>
            ✓ Submit Batch for Review {checklist.length > 0 && `(${checkedCount}/${checklist.length})`}
          </button>
        )}
        {task.social_phase === 'build' && localStatus === 'needs_revision' && (
          <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>↩ Start Revision</button>
        )}

        {/* REVIEW PHASE */}
        {task.social_phase === 'review' && (localStatus === 'not_started' || localStatus === 'in_progress' || localStatus === 'ready_for_review') && !showRevisionInput && (
          <>
            <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
              onClick={() => updateStatus('approved')} disabled={saving}>✅ Approve — Send to Client</button>
            <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
              onClick={() => setShowRevisionInput(true)}>↩ Request Revisions</button>
          </>
        )}
        {task.social_phase === 'review' && localStatus === 'needs_revision' && (
          <span style={{ fontSize: 12, color: '#8a6000', fontStyle: 'italic' }}>Revisions requested — waiting for creator</span>
        )}

        {/* CLIENT REVIEW PHASE */}
        {task.social_phase === 'client_review' && localStatus === 'not_started' && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start</button>
        )}
        {task.social_phase === 'client_review' && (localStatus === 'in_progress' || localStatus === 'not_started') && (
          <button className="btn btn-sm" style={{ background: '#e8f5fa', color: '#2a6e84', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
            onClick={sendToClient}><Mail size={13} /> Open Gmail to Send</button>
        )}
        {task.social_phase === 'client_review' && localStatus === 'in_progress' && !showRevisionInput && (
          <>
            <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
              onClick={() => updateStatus('approved')} disabled={saving}>✅ Client Approved</button>
            <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
              onClick={() => setShowRevisionInput(true)}>↩ Client Requested Revisions</button>
          </>
        )}

        {/* SCHEDULE PHASE — no lock; the red warning above shows until the client approves */}
        {task.social_phase === 'schedule' && ['not_started', 'goes_live'].includes(localStatus) && (
          <button className="btn btn-sm" style={{ background: 'var(--lightblue)', color: 'var(--navy)', border: 'none' }}
            onClick={() => updateStatus('in_progress')} disabled={saving}>▶ Start Scheduling</button>
        )}
        {task.social_phase === 'schedule' && localStatus === 'in_progress' && (
          <button className="btn btn-sm" style={{ background: '#e8f5eb', color: '#2e6e3a', border: 'none' }}
            onClick={() => {
              if (checklist.length > 0 && !allChecked) { toast(`Complete all ${checklist.length} checklist items first`, 'error'); return }
              updateStatus('approved')
            }} disabled={saving}>
            ✅ Mark as Scheduled {checklist.length > 0 && `(${checkedCount}/${checklist.length})`}
          </button>
        )}

        {/* Reset */}
        {localStatus !== 'not_started' && (
          <button className="btn btn-sm btn-ghost" style={{ fontSize: 11, marginLeft: 'auto' }}
            onClick={() => { if (window.confirm('Reset this phase back to Not Started?')) updateStatus('not_started') }} disabled={saving}>
            ↺ Reset
          </button>
        )}
      </div>

      {/* Revision input */}
      {showRevisionInput && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fff8e1', borderRadius: 10, border: '1px solid #f0d98a' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8a6000', marginBottom: 8 }}>What needs to be revised?</div>
          <textarea value={revisionNote} onChange={e => setRevisionNote(e.target.value)}
            placeholder="Be specific about which posts and what needs to change..."
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #f0d98a', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', resize: 'none', outline: 'none', minHeight: 70, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" style={{ background: '#c4962a', color: '#fff', border: 'none' }}
              onClick={submitRevision} disabled={saving || !revisionNote.trim()}>
              Send Back for Revisions
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => { setShowRevisionInput(false); setRevisionNote('') }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Checklist */}
      {checklist.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            Checklist — {checkedCount}/{checklist.length} {allChecked && '✅'}
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: 10, padding: '10px 12px', maxHeight: 300, overflowY: 'auto' }}>
            {checklist.map(item => (
              <label key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={item.checked} onChange={() => toggleItem(item.id)} style={{ marginTop: 2, flexShrink: 0 }} />
                <span style={{ color: item.checked ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: item.checked ? 'line-through' : 'none' }}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {focused && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>⏱ Time tracking</div>
          <TaskTimerButton task={task} />
        </div>
      )}

      {focused && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>💬 Comments</div>
          <TaskComments taskId={task.id} taskTitle={task.title} profiles={profiles} currentUser={currentUser} assignedBy={task.assigned_by} assignedTo={task.assigned_to} />
        </div>
      )}

      {showEdit && (
        <TaskModal task={task} clients={clients} profiles={profiles} currentUser={currentUser}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onUpdated && onUpdated() }} />
      )}
    </div>
  )
}

// ─── Reference docs row (used in both detail views) ─────────────────────────
function ReferenceDocsRow() {
  return (
    <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
        📚 Reference docs (open for guidelines)
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {SOCIAL_REFERENCE_DOCS.map(doc => (
          <a key={doc.url} href={doc.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
            <ExternalLink size={12} /> {doc.label}
          </a>
        ))}
      </div>
    </div>
  )
}

// ─── Social Batch Detail (full workflow page) ───────────────────────────────
function BatchDetail({ batch, clients, profiles, currentUser, onBack, onUpdated, onOpenSidePanel }) {
  const [tasks, setTasks] = useState([])
  const [activeSection, setActiveSection] = useState('workflow')

  const load = useCallback(async () => {
    const { data } = await supabase.from('tasks').select('*').eq('social_batch_id', batch.id).order('created_at')
    const phaseOrder = { build: 0, review: 1, client_review: 2, schedule: 3 }
    setTasks((data || []).sort((a, b) => (phaseOrder[a.social_phase] ?? 9) - (phaseOrder[b.social_phase] ?? 9)))
  }, [batch.id])

  useEffect(() => { load() }, [load])

  const client = clients.find(c => c.id === batch.client_id)
  const creator = profiles.find(p => p.id === batch.assigned_to)
  const assigner = profiles.find(p => p.id === batch.assigned_by)
  const phase = getPhase(batch.status)
  const planLabel = SOCIAL_PLANS.find(p => p.id === batch.plan)?.label || batch.plan

  const sections = ['workflow', 'brief']
  const sectionLabels = { workflow: '📋 Workflow', brief: '📝 Brief' }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', marginLeft: 'auto' }}
          onClick={async () => {
            if (!window.confirm('Delete this batch and all 4 associated tasks? This cannot be undone.')) return
            await supabase.from('tasks').delete().eq('social_batch_id', batch.id)
            await supabase.from('social_batches').delete().eq('id', batch.id)
            onBack()
          }}>
          🗑 Delete Batch
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
              📱 {client?.name || ''} — {batch.month}
            </h1>
            {phase && <span className="status-pill" style={{ background: phase.bg, color: phase.color }}>{phase.icon} {phase.label}</span>}
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 13, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            <span>{planLabel}</span>
            {creator && <span>📱 {creator.full_name}</span>}
            {assigner && assigner.id !== creator?.id && <span>👤 Assigned by {assigner.full_name}</span>}
            <span>{batch.carousel_count} carousels · {batch.reel_count} reels · {batch.static_count} statics</span>
            {batch.draft_due_date && <span>📅 Draft due {safeFmt(batch.draft_due_date, 'MMM d')}</span>}
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {sections.map(s => (
          <button key={s} className={`tab ${activeSection === s ? 'active' : ''}`} onClick={() => setActiveSection(s)}>
            {sectionLabels[s]}
          </button>
        ))}
      </div>

      {activeSection === 'workflow' && (
        <div>
          <ReferenceDocsRow />
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Each phase must be completed in order. The creator builds the batch, then it gets reviewed, then sent to the client, then scheduled.
          </div>
          {tasks.map(task => (
            <SocialPhaseTask
              key={task.id}
              task={task}
              batch={batch}
              profiles={profiles}
              clients={clients}
              currentUser={currentUser}
              onUpdated={load}
              onOpenInPanel={onOpenSidePanel ? () => onOpenSidePanel(task, tasks) : null}
            />
          ))}
        </div>
      )}

      {activeSection === 'brief' && (
        <div className="card card-body">
          {[
            { label: "What's Working", value: batch.whats_working, bg: '#e8f5eb', border: '#b8d8c0', color: '#2e6e3a', prefix: '✓' },
            { label: "What's NOT Working", value: batch.whats_not_working, bg: '#fdf0f0', border: '#f0c4c4', color: 'var(--red)', prefix: '✗' },
          ].map(f => f.value ? (
            <div key={f.label} style={{ marginBottom: 16, padding: '12px 14px', background: f.bg, border: `1px solid ${f.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: f.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{f.prefix} {f.label}</div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{f.value}</p>
            </div>
          ) : null)}
          {batch.brief_notes && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Notes</div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{batch.brief_notes}</p>
            </div>
          )}
          {!batch.whats_working && !batch.whats_not_working && !batch.brief_notes && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No brief notes yet</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Side panel for single task (opens when task is clicked) ────────────────
export function SocialPhaseSidePanel({ task, batch, allBatchTasks, profiles, clients, currentUser, onSwitchTask, onClose, onUpdated, onOpenFullWorkflow }) {
  const phaseOrder = { build: 0, review: 1, client_review: 2, schedule: 3 }
  const phaseLabels = {
    build:         { icon: '📱', label: 'Build' },
    review:        { icon: '👁', label: 'Review' },
    client_review: { icon: '📨', label: 'Client' },
    schedule:      { icon: '📅', label: 'Schedule' },
  }
  const sortedTasks = (allBatchTasks || []).slice().sort((a, b) => (phaseOrder[a.social_phase] ?? 9) - (phaseOrder[b.social_phase] ?? 9))

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 580, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header — fixed at top */}
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Batch · {batch?.month}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {onOpenFullWorkflow && (
              <button onClick={onOpenFullWorkflow} title="View full workflow"
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                📋 Full Workflow
              </button>
            )}
            <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* Phase stepper — fixed below header */}
        {sortedTasks.length > 0 && (
          <div style={{ display: 'flex', gap: 4, padding: '10px 16px', background: 'var(--cream)', borderBottom: '1px solid var(--border-light)', overflowX: 'auto', flexShrink: 0 }}>
            {sortedTasks.map(t => {
              const p = phaseLabels[t.social_phase] || { icon: '•', label: t.social_phase }
              const isActive = t.id === task.id
              const isDone = ['approved', 'live'].includes(t.status)
              return (
                <button key={t.id} onClick={() => onSwitchTask && onSwitchTask(t)}
                  style={{
                    flex: 1, padding: '6px 8px', borderRadius: 8, fontSize: 11, border: 'none', cursor: 'pointer',
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? 'var(--navy)' : (isDone ? '#e8f5eb' : 'transparent'),
                    color: isActive ? '#fff' : (isDone ? '#2e6e3a' : 'var(--text-muted)'),
                    minWidth: 90, whiteSpace: 'nowrap',
                  }}>
                  {isDone ? '✓ ' : `${p.icon} `}{p.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Scrollable body */}
        <div style={{ padding: '18px 20px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <ReferenceDocsRow />
          <SocialPhaseTask task={task} batch={batch} profiles={profiles} clients={clients} currentUser={currentUser} onUpdated={onUpdated} focused />
        </div>
      </div>
    </div>
  )
}

// ─── Reusable alert banner ─────────────────────────────────────────────────
function ClientAlertsBanner({ title, subtitle, items, onOpenClientProfile }) {
  const [expanded, setExpanded] = useState(items.length <= 3)
  const shown = expanded ? items : items.slice(0, 3)
  return (
    <div style={{ background: '#fff8e1', border: '1.5px solid #f0d98a', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: shown.length > 0 ? 10 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#8a6000', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⏰ {title}
          </div>
          <div style={{ fontSize: 12, color: '#8a6000', opacity: 0.85, marginTop: 2 }}>{subtitle}</div>
        </div>
        {items.length > 3 && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ background: 'transparent', border: 'none', color: '#8a6000', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            {expanded ? 'Show less' : `Show all ${items.length}`}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {shown.map(({ client, alert }) => (
          <button key={client.id}
            onClick={() => onOpenClientProfile && onOpenClientProfile(client.id)}
            style={{
              background: '#fff', border: '1px solid #f0d98a', color: '#8a6000',
              padding: '5px 10px', borderRadius: 50, fontSize: 12, cursor: 'pointer',
              fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5,
            }}>
            {client.name}
            <ChevronRight size={11} />
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Main list view ────────────────────────────────────────────────────────
export default function SocialBatchesView({ clients, profiles, currentUser, profile, selectedBatchId, selectedBatchTaskId, onClearBatchId, onOpenClientProfile }) {
  const [batches, setBatches] = useState([])
  const [allBlogs, setAllBlogs] = useState([])
  const [allBatchesForAlerts, setAllBatchesForAlerts] = useState([])
  const [selected, setSelected] = useState(null)
  const [sidePanelTask, setSidePanelTask] = useState(null)
  const [sidePanelBatch, setSidePanelBatch] = useState(null)
  const [sidePanelAllTasks, setSidePanelAllTasks] = useState([])
  const [showNew, setShowNew] = useState(false)
  const [filterClient, setFilterClient] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [loading, setLoading] = useState(true)

  const canCreate = ['supervising_ad', 'ad', 'am'].includes(profile?.role)

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('social_batches').select('*, client:clients(name), creator:profiles!social_batches_assigned_to_fkey(full_name)').order('created_at', { ascending: false })
    if (filterClient) q = q.eq('client_id', filterClient)
    if (filterStatus) q = q.eq('status', filterStatus)
    const { data } = await q
    setBatches(data || [])
    // Unfiltered for alert math
    const { data: allBatchesData } = await supabase.from('social_batches').select('id, client_id, status, updated_at, created_at')
    const { data: allBlogsData } = await supabase.from('blog_assignments').select('id, client_id, status, updated_at, created_at')
    setAllBatchesForAlerts(allBatchesData || [])
    setAllBlogs(allBlogsData || [])
    setLoading(false)
  }, [filterClient, filterStatus])

  const clientsNeedingSocial = useMemo(() => {
    const alertMap = computeAllClientAlerts(clients, allBlogs, allBatchesForAlerts)
    const result = []
    for (const [clientId, alerts] of alertMap.entries()) {
      const socialAlert = alerts.find(a => a.type === 'social_cadence')
      if (socialAlert) {
        const client = clients.find(c => c.id === clientId)
        if (client) result.push({ client, alert: socialAlert })
      }
    }
    return result
  }, [clients, allBlogs, allBatchesForAlerts])

  useEffect(() => { load() }, [load])

  // When navigated from task click: fetch batch + all its phase tasks, open side panel to the specific task
  useEffect(() => {
    if (!selectedBatchId) return
    ;(async () => {
      // Fetch batch directly (don't depend on the filtered list)
      const { data: foundBatch } = await supabase.from('social_batches').select('*, client:clients(name), creator:profiles!social_batches_assigned_to_fkey(full_name)').eq('id', selectedBatchId).maybeSingle()
      if (!foundBatch) { onClearBatchId && onClearBatchId(); return }

      const { data: phaseTasks } = await supabase.from('tasks').select('*').eq('social_batch_id', foundBatch.id).order('created_at')
      const tasks = phaseTasks || []

      // Open to the specific task if given, otherwise the user's task, otherwise first
      let target = null
      if (selectedBatchTaskId) {
        target = tasks.find(t => t.id === selectedBatchTaskId)
      }
      if (!target) {
        target = tasks.find(t => t.assigned_to === currentUser.id && !['approved', 'live'].includes(t.status))
      }
      if (!target) {
        target = tasks.find(t => !['approved', 'live'].includes(t.status)) || tasks[0]
      }

      if (target) {
        setSidePanelTask(target)
        setSidePanelBatch(foundBatch)
        setSidePanelAllTasks(tasks)
      }
      onClearBatchId && onClearBatchId()
    })()
  }, [selectedBatchId, selectedBatchTaskId, currentUser.id, onClearBatchId])

  const reloadSidePanel = async () => {
    if (!sidePanelTask) return
    // Reload all phase tasks (status may have changed)
    const { data: phaseTasks } = await supabase.from('tasks').select('*').eq('social_batch_id', sidePanelBatch.id).order('created_at')
    setSidePanelAllTasks(phaseTasks || [])
    const refreshed = (phaseTasks || []).find(t => t.id === sidePanelTask.id)
    if (refreshed) setSidePanelTask(refreshed)
    const { data: b } = await supabase.from('social_batches').select('*').eq('id', sidePanelBatch.id).single()
    if (b) setSidePanelBatch(b)
    load()
  }

  const openFullWorkflowFromPanel = () => {
    if (!sidePanelBatch) return
    setSelected(sidePanelBatch)
    setSidePanelTask(null)
    setSidePanelBatch(null)
    setSidePanelAllTasks([])
  }

  if (selected) {
    return (
      <>
        <BatchDetail batch={selected} clients={clients} profiles={profiles} currentUser={currentUser}
          onBack={() => { setSelected(null); load() }}
          onUpdated={load}
          onOpenSidePanel={(task, allTasks) => { setSidePanelTask(task); setSidePanelBatch(selected); setSidePanelAllTasks(allTasks) }}
        />
        {sidePanelTask && sidePanelBatch && (
          <SocialPhaseSidePanel
            task={sidePanelTask}
            batch={sidePanelBatch}
            allBatchTasks={sidePanelAllTasks}
            profiles={profiles}
            clients={clients}
            currentUser={currentUser}
            onSwitchTask={t => setSidePanelTask(t)}
            onClose={() => { setSidePanelTask(null); setSidePanelBatch(null); setSidePanelAllTasks([]); load() }}
            onUpdated={reloadSidePanel}
            onOpenFullWorkflow={null}
          />
        )}
      </>
    )
  }

  return (
    <div>
      {clientsNeedingSocial.length > 0 && (
        <ClientAlertsBanner
          title="Clients needing a new social batch"
          subtitle={`${clientsNeedingSocial.length} client${clientsNeedingSocial.length === 1 ? '' : 's'} ${clientsNeedingSocial.length === 1 ? 'is' : 'are'} running low on scheduled posts`}
          items={clientsNeedingSocial}
          onOpenClientProfile={onOpenClientProfile}
        />
      )}
      <div className="filters-bar">
        <select className="filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Phases</option>
          {SOCIAL_PHASES.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          {canCreate && <button className="btn btn-gold" onClick={() => setShowNew(true)}><Plus size={15} /> New Social Batch</button>}
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
        : batches.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
            <h3>No social batches yet</h3>
            <p>{canCreate ? 'Create your first batch to brief a content creator' : 'No batches have been assigned yet'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {batches.map(batch => {
              const phase = getPhase(batch.status)
              const planLabel = SOCIAL_PLANS.find(p => p.id === batch.plan)?.label || batch.plan
              return (
                <div key={batch.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${phase?.color || 'var(--border)'}` }}
                  onClick={() => setSelected(batch)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
                  <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {batch.client?.name || '—'} · {batch.month}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                        <span>{planLabel}</span>
                        {batch.creator?.full_name && <span>📱 {batch.creator.full_name}</span>}
                        <span>{batch.carousel_count}C · {batch.reel_count}R · {batch.static_count}S</span>
                        {batch.draft_due_date && <span>📅 {safeFmt(batch.draft_due_date, 'MMM d')}</span>}
                      </div>
                    </div>
                    {phase && <span className="status-pill" style={{ background: phase.bg, color: phase.color, flexShrink: 0 }}>{phase.icon} {phase.label}</span>}
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      {showNew && <NewBatchModal clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load() }} />}

      {sidePanelTask && sidePanelBatch && (
        <SocialPhaseSidePanel
          task={sidePanelTask}
          batch={sidePanelBatch}
          allBatchTasks={sidePanelAllTasks}
          profiles={profiles}
          clients={clients}
          currentUser={currentUser}
          onSwitchTask={t => setSidePanelTask(t)}
          onClose={() => { setSidePanelTask(null); setSidePanelBatch(null); setSidePanelAllTasks([]); load() }}
          onUpdated={reloadSidePanel}
          onOpenFullWorkflow={openFullWorkflowFromPanel}
        />
      )}
    </div>
  )
}